import React, { useEffect, useState, useCallback } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminGetStatuses,
  adminGetMembershipSources,
} from "../../lib/studioApi";
import Avatar from "../studio/Avatar";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  KeyRound,
  UserCheck,
  UserX,
  Copy,
  FlaskConical,
  ArrowDownUp,
  ListFilter,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  "Inquiry Received": "bg-white/10 text-white/70",
  "Call Scheduled": "bg-blue-500/20 text-blue-300",
  Songwriting: "bg-purple-500/20 text-purple-300",
  Production: "bg-amber-500/20 text-amber-300",
  Mixing: "bg-orange-500/20 text-orange-300",
  Mastering: "bg-pink-500/20 text-pink-300",
  "Ready For Release": "bg-emerald-500/20 text-emerald-300",
  Completed: "bg-paul-yellow/20 text-paul-yellow",
};

// Admin-only enum — paired with backend MEMBERSHIP_SOURCES. Keep labels +
// pill styles here so the rest of the panel reads cleanly. Note: this list
// MUST never leak into any artist-facing component.
const SOURCE_META = {
  studio_membership: {
    label: "Studio Membership",
    short: "Membership",
    pill: "bg-paul-yellow/15 text-paul-yellow",
  },
  studio_plus: {
    label: "Studio Plus",
    short: "Plus",
    pill: "bg-paul-yellow/25 text-paul-yellow",
  },
  private_studio_production: {
    label: "Private Studio Production",
    short: "Private",
    pill: "bg-purple-500/20 text-purple-300",
  },
  admin_created: {
    label: "Admin Created",
    short: "Admin",
    pill: "bg-white/10 text-white/70",
  },
  test_account: {
    label: "Test Account",
    short: "Test",
    pill: "bg-red-500/20 text-red-300",
  },
};
const SOURCE_FILTER_OPTIONS = ["all", ...Object.keys(SOURCE_META)];

function generatePassword() {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function UserForm({ open, onOpenChange, statuses, sources, editing, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() =>
    editing
      ? {
          name: editing.name || "",
          email: editing.email || "",
          artistName: editing.artistName || "",
          status: editing.status || "Inquiry Received",
          membershipSource: editing.membershipSource || "admin_created",
          password: "",
        }
      : {
          name: "",
          email: "",
          artistName: "",
          status: "Inquiry Received",
          membershipSource: "admin_created",
          password: generatePassword(),
        },
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!isEdit && (!form.password || form.password.length < 6)) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name,
          email: form.email,
          artistName: form.artistName,
          status: form.status,
          membershipSource: form.membershipSource,
        };
        if (form.password) payload.password = form.password;
        await adminUpdateUser(editing.id, payload);
        toast.success("User updated");
      } else {
        await adminCreateUser(form);
        toast.success(
          form.membershipSource === "test_account"
            ? "Test account created — no welcome email sent"
            : "User created — welcome email sent",
        );
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#10162a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">
            {isEdit ? "Edit Studio User" : "Create Studio User"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Full Name
            </label>
            <Input
              data-testid="studio-user-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Email
            </label>
            <Input
              data-testid="studio-user-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Artist / Stage Name
            </label>
            <Input
              data-testid="studio-user-artist-input"
              value={form.artistName}
              onChange={(e) =>
                setForm({ ...form, artistName: e.target.value })
              }
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Artist Status
            </label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
            >
              <SelectTrigger
                data-testid="studio-user-status-select"
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider flex items-center justify-between">
              <span>Membership Source</span>
              <span className="text-[10px] text-white/30 normal-case">Admin only · hidden from artists</span>
            </label>
            <Select
              value={form.membershipSource}
              onValueChange={(v) => setForm({ ...form, membershipSource: v })}
            >
              <SelectTrigger
                data-testid="studio-user-source-select"
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(sources && sources.length ? sources : Object.keys(SOURCE_META)).map((s) => (
                  <SelectItem key={s} value={s}>
                    {SOURCE_META[s]?.label || s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.membershipSource === "test_account" && (
              <p className="text-[11px] text-red-300/80 mt-1 flex items-center gap-1.5">
                <FlaskConical className="w-3 h-3" />
                Excluded from broadcast emails &amp; analytics. Welcome email will be skipped.
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider flex items-center justify-between">
              <span>
                {isEdit ? "Reset Password (optional)" : "Initial Password"}
              </span>
              <button
                type="button"
                onClick={() =>
                  setForm({ ...form, password: generatePassword() })
                }
                className="text-paul-yellow hover:underline normal-case text-[11px] font-semibold"
              >
                Generate
              </button>
            </label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="studio-user-password-input"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                placeholder={isEdit ? "Leave blank to keep" : ""}
                className="font-mono text-sm bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
              />
              {form.password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(form.password);
                    toast.success("Copied");
                  }}
                  className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              )}
            </div>
            {!isEdit && (
              <p className="text-xs text-white/40 mt-1">
                Login link + password will be emailed to the user.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            data-testid="studio-user-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
            style={{ color: "#0b1535" }}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create user"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StudioUsersPanel() {
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  // "recent" = createdAt desc (default), "source" = membershipSource A→Z
  const [sortKey, setSortKey] = useState("recent");

  const load = useCallback(async () => {
    try {
      const [u, s, src] = await Promise.all([
        adminListUsers(),
        adminGetStatuses(),
        adminGetMembershipSources().catch(() => Object.keys(SOURCE_META)),
      ]);
      setUsers(u);
      setStatuses(s);
      setSources(src);
    } catch (e) {
      console.error("Failed to load studio users:", e);
      toast.error("Failed to load studio users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (u) => {
    try {
      await adminUpdateUser(u.id, { active: !u.active });
      toast.success(u.active ? "Account deactivated" : "Account reactivated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (u) => {
    if (
      !window.confirm(
        `Delete ${u.name}? This also removes all their posts. This cannot be undone.`,
      )
    )
      return;
    try {
      await adminDeleteUser(u.id);
      toast.success("User deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const resetPassword = async (u) => {
    const pw = generatePassword();
    if (
      !window.confirm(
        `Reset password for ${u.name} to:\n\n${pw}\n\nCopy it now — they won't receive a new email.`,
      )
    )
      return;
    try {
      await adminUpdateUser(u.id, { password: pw });
      navigator.clipboard.writeText(pw).catch(() => {});
      toast.success("Password reset and copied to clipboard");
    } catch {
      toast.error("Reset failed");
    }
  };

  // Filter + sort runs client-side — the admin user list is small enough
  // (well under 1k rows for the foreseeable future) that doing this in the
  // browser keeps the API surface unchanged and the UI snappy.
  const displayed = React.useMemo(() => {
    let rows = users;
    if (sourceFilter !== "all") {
      rows = rows.filter((u) => u.membershipSource === sourceFilter);
    }
    if (sortKey === "source") {
      rows = [...rows].sort((a, b) =>
        (a.membershipSource || "").localeCompare(b.membershipSource || ""),
      );
    }
    return rows;
  }, [users, sourceFilter, sortKey]);

  const sourceCount = (key) =>
    key === "all"
      ? users.length
      : users.filter((u) => u.membershipSource === key).length;

  return (
    <div data-testid="studio-users-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-white">Studio Users</h2>
          <p className="text-white/50 text-sm mt-1">
            {displayed.length === users.length
              ? `${users.length} ${users.length === 1 ? "artist" : "artists"} with portal access`
              : `${displayed.length} of ${users.length} shown`}
          </p>
        </div>
        <Button
          data-testid="add-studio-user-btn"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
          style={{ color: "#0b1535" }}
        >
          <Plus className="w-4 h-4 mr-2" />
          New User
        </Button>
      </div>

      {/* Source filter + sort toolbar — admin-only segmentation controls. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-xs text-white/40 uppercase tracking-wider">
          <ListFilter className="w-3.5 h-3.5" />
          Source
        </div>
        <div
          data-testid="studio-users-source-filter"
          className="flex flex-wrap gap-1.5"
        >
          {SOURCE_FILTER_OPTIONS.map((key) => {
            const meta = SOURCE_META[key];
            const active = sourceFilter === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`studio-users-source-chip-${key}`}
                onClick={() => setSourceFilter(key)}
                className={`text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  active
                    ? "bg-paul-yellow text-navy"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}
                style={active ? { color: "#0b1535" } : undefined}
              >
                {key === "all" ? "All" : meta?.short || key}
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    active ? "bg-navy/15" : "bg-white/10"
                  }`}
                  style={active ? { color: "#0b1535" } : undefined}
                >
                  {sourceCount(key)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto">
          <button
            type="button"
            data-testid="studio-users-sort-toggle"
            onClick={() =>
              setSortKey((k) => (k === "recent" ? "source" : "recent"))
            }
            className="text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white flex items-center gap-1.5"
          >
            <ArrowDownUp className="w-3 h-3" />
            Sort: {sortKey === "recent" ? "Newest" : "Source A→Z"}
          </button>
        </div>
      </div>

      <div className="bg-[#0f1322] rounded-xl shadow-sm border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-white/60">Name</TableHead>
              <TableHead className="text-white/60">Email</TableHead>
              <TableHead className="text-white/60">Plan</TableHead>
              <TableHead className="text-white/60">Source</TableHead>
              <TableHead className="text-white/60">Status</TableHead>
              <TableHead className="text-white/60">Joined</TableHead>
              <TableHead className="text-right text-white/60">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="text-center text-white/40 py-10"
                >
                  <Loader2 className="w-5 h-5 animate-spin inline-block" />
                </TableCell>
              </TableRow>
            ) : displayed.length === 0 ? (
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="text-center text-white/40 py-10"
                >
                  {users.length === 0
                    ? "No studio users yet. Create one to get started."
                    : "No users match this filter."}
                </TableCell>
              </TableRow>
            ) : (
              displayed.map((u) => (
                <TableRow
                  key={u.id}
                  data-testid={`studio-user-row-${u.id}`}
                  className={`border-white/5 hover:bg-white/[0.03] ${
                    u.membershipSource === "test_account"
                      ? "bg-red-500/[0.04]"
                      : ""
                  }`}
                >
                  <TableCell className="font-semibold text-white">
                    <div className="flex items-center gap-3">
                      <Avatar user={u} size="sm" showDot ringColor="border-[#0f1322]" testIdPrefix={`studio-user-avatar-${u.id}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="truncate">{u.artistName || u.name}</span>
                          {u.membershipSource === "test_account" && (
                            <span
                              data-testid={`studio-user-test-badge-${u.id}`}
                              className="text-[9px] uppercase tracking-wider font-extrabold bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded flex items-center gap-1"
                            >
                              <FlaskConical className="w-2.5 h-2.5" />
                              Test
                            </span>
                          )}
                          {u.tier === "studio_plus" && (
                            <span className="text-[9px] uppercase tracking-wider font-extrabold bg-paul-yellow text-navy px-1.5 py-0.5 rounded" style={{ color: "#0b1535" }}>
                              Plus
                            </span>
                          )}
                          {u.accountSource === "subscription" ? (
                            <span className="text-[9px] uppercase tracking-wider font-bold bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">
                              Subscriber
                            </span>
                          ) : (
                            <span className="text-[9px] uppercase tracking-wider font-bold bg-white/10 text-white/60 px-1.5 py-0.5 rounded">
                              Invited
                            </span>
                          )}
                          {!u.active && (
                            <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        {u.artistName && u.artistName !== u.name && (
                          <div className="text-xs text-white/40 font-normal">
                            {u.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-white/70">{u.email}</TableCell>
                  <TableCell className="text-white/70">
                    {u.tier === "studio_plus" ? (
                      <div>
                        <div className="text-sm font-semibold text-paul-yellow">Studio Plus</div>
                        <div className="text-[10px] text-white/40 capitalize">{u.billingType || "—"}</div>
                      </div>
                    ) : u.tier === "studio_membership" ? (
                      <div>
                        <div className="text-sm font-semibold">Studio Membership</div>
                        <div className="text-[10px] text-white/40 capitalize">{u.billingType || "—"}</div>
                      </div>
                    ) : (
                      <span className="text-white/40 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      data-testid={`studio-user-source-${u.id}`}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                        SOURCE_META[u.membershipSource]?.pill ||
                        "bg-white/10 text-white/60"
                      }`}
                    >
                      {SOURCE_META[u.membershipSource]?.short ||
                        u.membershipSource ||
                        "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                        STATUS_BADGE[u.status] || "bg-white/10 text-white/70"
                      }`}
                    >
                      {u.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-white/50 text-sm">
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditing(u);
                          setFormOpen(true);
                        }}
                        title="Edit"
                        className="text-white/70 hover:text-paul-yellow hover:bg-white/5"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => resetPassword(u)}
                        title="Reset password"
                        className="text-white/70 hover:text-paul-yellow hover:bg-white/5"
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleActive(u)}
                        title={u.active ? "Deactivate" : "Reactivate"}
                        className="text-white/70 hover:text-paul-yellow hover:bg-white/5"
                      >
                        {u.active ? (
                          <UserX className="w-4 h-4" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(u)}
                        title="Delete"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UserForm
        key={editing?.id || (formOpen ? "new" : "closed")}
        open={formOpen}
        onOpenChange={setFormOpen}
        statuses={statuses}
        sources={sources}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
}
